import React, { useState, useEffect, useRef } from 'react';
import { 
  ShoppingCart, 
  Plus, 
  Search, 
  Trash2, 
  Edit3, 
  Check, 
  Upload, 
  X, 
  ArrowLeft,
  ChevronDown,
  FileText,
  AlertCircle
} from 'lucide-react';
import { ProductsController } from './products/products.controller';
import { ProductItem } from './products/products.types';
import { CategoriesController } from './categories/categories.controller';
import { SettingsController } from './settings/settings.controller';
import { SectorItem } from './settings/settings.types';
import { useApp } from '../context/AppContext';

export default function ProductsListView() {
  const { currentUser } = useApp();
  const isAdm = currentUser?.role === 'ADM';
  const canAddProduto = isAdm || currentUser?.permissions?.add?.produto === true;
  const canEditProduto = isAdm || currentUser?.permissions?.edit?.produto === true;
  const canDeleteProduto = isAdm || currentUser?.permissions?.delete?.produto === true;

  const [viewState, setViewState] = useState<'LIST' | 'DETAIL'>('LIST');

  // Hardcoded coifa details sync with ProductsController
  const [productsList, setProductsList] = useState<ProductItem[]>(() => ProductsController.list());
  const [registeredAccessoryProducts, setRegisteredAccessoryProducts] = useState<ProductItem[]>([]);
  const [registeredPieceProducts, setRegisteredPieceProducts] = useState<ProductItem[]>([]);
  const [productionSectors, setProductionSectors] = useState<SectorItem[]>([]);
  const [selectedProductId, setSelectedProductId] = useState<string>('621');
  const [editingProduct, setEditingProduct] = useState<ProductItem | null>(null);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);

  // States for search and filter controls
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('Todas');

  // Fetch list of registered categories in the database
  const registeredCategories = CategoriesController.list();

  // Selected state lists for accessory and pieces tags inside the builder card
  const [selectedAccessories, setSelectedAccessories] = useState<string[]>([]);
  const [selectedPieces, setSelectedPieces] = useState<string[]>([]);

  // Base64 storage states for images
  const [imageState, setImageState] = useState<{
    productImage: string;
    projectImage1: string;
    projectImage2: string;
  }>({
    productImage: '',
    projectImage1: '',
    projectImage2: ''
  });

  // UI alert feedback states
  const [alert, setAlert] = useState<{ type: 'success' | 'danger' | 'warning' | 'info'; msg: string } | null>(null);

  // Lightbox expanded image state
  const [expandedImage, setExpandedImage] = useState<{ src: string; title: string } | null>(null);

  // Helper to click an accessory/piece and redirect to its product screen, or auto-create it if not found
  const handleAccessoryOrPieceClick = async (itemText: string, defaultCategory: 'Acessórios' | 'Partes') => {
    const all = await ProductsController.listFromServer('Todas');
    const found = all.find(p => 
      p.name.trim().toLowerCase() === itemText.trim().toLowerCase() ||
      p.model.trim().toLowerCase() === itemText.trim().toLowerCase()
    );

    if (found) {
      setSelectedProductId(found.id);
      setViewState('DETAIL');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      triggerAlert('info', `Exibindo informações detalhadas de: ${found.name}`);
    } else {
      // Create new accessory or part product on the fly!
      const tempForm = new FormData();
      tempForm.set('name', itemText);
      tempForm.set('category', defaultCategory);
      tempForm.set('model', `${itemText} Standard`);
      tempForm.set('desc', `Produto cadastrado automaticamente via associação de ${defaultCategory === 'Acessórios' ? 'acessório' : 'parte estrutural'}.\nEdite para acrescentar fotos e cronograma específicos.`);
      
      const result = await ProductsController.handleSaveToServer(null, tempForm, {
        accessories: [],
        pieces: [],
        productImageBase64: '',
        projectImage1Base64: '',
        projectImage2Base64: ''
      });

      if (result.success && result.product) {
        // Refresh local state list
        const updated = await ProductsController.listFromServer(categoryFilter, searchQuery);
        setProductsList(updated);
        setSelectedProductId(result.product.id);
        setViewState('DETAIL');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        triggerAlert('success', `Novo produto gerado automaticamente para "${itemText}" em "${defaultCategory}"! Edite para inserir detalhes.`);
      } else {
        triggerAlert('danger', `Não foi possível abrir o produto associado.`);
      }
    }
  };

  // Custom accessory/parts inline inputs
  const [customAccessoryInput, setCustomAccessoryInput] = useState<string>('');
  const [showCustomAccessory, setShowCustomAccessory] = useState<boolean>(false);

  const [customPieceInput, setCustomPieceInput] = useState<string>('');
  const [showCustomPiece, setShowCustomPiece] = useState<boolean>(false);

  const dragAreaRef1 = useRef<HTMLDivElement>(null);
  const dragAreaRef2 = useRef<HTMLDivElement>(null);
  const dragAreaRef3 = useRef<HTMLDivElement>(null);

  const selectedProduct = productsList.find(p => p.id === selectedProductId) || productsList[0];

  // Reload products whenever filters change
  useEffect(() => {
    let active = true;
    async function load() {
      const list = await ProductsController.listFromServer(categoryFilter, searchQuery);
      const accList = await ProductsController.getAccessoryProductsFromServer();
      const partList = await ProductsController.getPartProductsFromServer();
      const sectorsList = await SettingsController.getSectorsFromServer();
      if (active) {
        setProductsList(list);
        setRegisteredAccessoryProducts(accList);
        setRegisteredPieceProducts(partList);
        const prodOnly = sectorsList
          .filter(s => s.isProductionSequence !== false)
          .sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));
        setProductionSectors(prodOnly);
      }
    }
    load();
    return () => { active = false; };
  }, [categoryFilter, searchQuery, viewState, showEditModal]);

  const triggerAlert = (type: 'success' | 'danger' | 'warning' | 'info', msg: string) => {
    setAlert({ type, msg });
    setTimeout(() => {
      setAlert(null);
    }, 6000);
  };

  const handleRowClick = (id: string) => {
    setSelectedProductId(id);
    setViewState('DETAIL');
  };

  const handleAddClick = () => {
    const defaultSteps = productionSectors.length > 0
      ? productionSectors.map(s => s.name)
      : ['Corte', 'Dobra', 'Solda', 'Acabamento', 'Acessórios', 'Embalagem', 'Logística'];

    setEditingProduct({
      id: '',
      name: '',
      category: 'Coifas',
      model: '',
      desc: '',
      productImage: '',
      projectImage1: '',
      projectImage2: '',
      accessories: [],
      pieces: [],
      productionSequence: defaultSteps
    });
    setSelectedAccessories([]);
    setSelectedPieces([]);
    setImageState({
      productImage: '',
      projectImage1: '',
      projectImage2: ''
    });
    setShowEditModal(true);
  };

  const handleEditClick = (p: ProductItem) => {
    setEditingProduct(p);
    setSelectedAccessories(p.accessories || []);
    setSelectedPieces(p.pieces || []);
    setImageState({
      productImage: p.productImage || '',
      projectImage1: p.projectImage1 || '',
      projectImage2: p.projectImage2 || ''
    });
    setShowEditModal(true);
  };

  // Handles drag/drop file entries
  const handleFileDrop = (e: React.DragEvent<HTMLDivElement>, fieldKey: 'productImage' | 'projectImage1' | 'projectImage2') => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processImageFile(e.dataTransfer.files[0], fieldKey);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>, fieldKey: 'productImage' | 'projectImage1' | 'projectImage2') => {
    if (e.target.files && e.target.files[0]) {
      processImageFile(e.target.files[0], fieldKey);
    }
  };

  const processImageFile = (file: File, fieldKey: 'productImage' | 'projectImage1' | 'projectImage2') => {
    // Perform limit size check (550 KB)
    const check = ProductsController.validateImageSize(file);
    if (!check.valid) {
      triggerAlert('danger', check.reason || 'Arquivo muito grande.');
      return;
    }

    // Clean up old image simulation message
    if (imageState[fieldKey]) {
      console.log(`[STORAGE SERVER CLEANUP] Imagem antiga substituída e eliminada do banco de dados e diretório do servidor (/uploads/products/) para o campo ${fieldKey}.`);
    }

    const reader = new FileReader();
    reader.onload = () => {
      setImageState(prev => ({
        ...prev,
        [fieldKey]: reader.result as string
      }));
      triggerAlert('success', `Imagem "${file.name}" carregada com sucesso!`);
    };
    reader.readAsDataURL(file);
  };

  // Simulates completely purging or deleting an image from database and assets folder on the server
  const handleClearImage = (fieldKey: 'productImage' | 'projectImage1' | 'projectImage2') => {
    if (imageState[fieldKey]) {
      console.log(`[STORAGE SERVER CLEANUP] Liberando armazenamento. A imagem no campo "${fieldKey}" foi removida do banco de dados e o arquivo físico foi apagado da pasta de uploads no servidor (/uploads/products/).`);
      setImageState(prev => ({ ...prev, [fieldKey]: '' }));
      triggerAlert('warning', 'Imagem deletada fisicamente do servidor de arquivos e removida do banco.');
    }
  };

  const handleSaveProductSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editingProduct) return;

    const form = e.currentTarget;
    const formData = new FormData(form);

    // Save using standard product controller MVC mapping
    const result = await ProductsController.handleSaveToServer(
      editingProduct.id || null, 
      formData, 
      {
        accessories: selectedAccessories,
        pieces: selectedPieces,
        productImageBase64: imageState.productImage,
        projectImage1Base64: imageState.projectImage1,
        projectImage2Base64: imageState.projectImage2
      }
    );

    if (result.success && result.product) {
      triggerAlert('success', `Produto "${result.product.name}" salvo e atualizado de forma segura no banco de dados e servidor!`);
      const updated = await ProductsController.listFromServer(categoryFilter, searchQuery);
      setProductsList(updated);
      setSelectedProductId(result.product.id);
      setShowEditModal(false);
      setViewState('LIST');
    } else {
      triggerAlert('danger', result.error || 'Não foi possível salvar o produto.');
    }
  };

  const handleDeleteProduct = async (id: string, name: string) => {
    if (window.confirm(`Deseja realmente remover o produto "${name}" e expurgar todos os arquivos de desenho do servidor e banco de dados?`)) {
      const result = await ProductsController.handleDeleteOnServer(id);
      if (result.success) {
        triggerAlert('warning', `Produto "${name}" e todos os seus arquivos de desenho técnico foram excluídos com sucesso.`);
        setViewState('LIST');
        // fallback select
        const remains = await ProductsController.listFromServer(categoryFilter, searchQuery);
        setProductsList(remains);
        if (remains.length > 0) {
          setSelectedProductId(remains[0].id);
        }
      } else {
        triggerAlert('danger', result.error || 'Erro ao excluir o produto.');
      }
    }
  };

  // Accessories list options have been migrated to dynamic React states (registeredAccessoryProducts and registeredPieceProducts)

  const handleAddAccessory = (name: string) => {
    if (!name.trim()) return;
    if (selectedAccessories.includes(name.trim())) {
      triggerAlert('warning', 'Este acessório já está inserido no produto.');
      return;
    }
    setSelectedAccessories([...selectedAccessories, name.trim()]);
    setCustomAccessoryInput('');
    setShowCustomAccessory(false);
  };

  const handleRemoveAccessory = (name: string) => {
    setSelectedAccessories(selectedAccessories.filter(a => a !== name));
  };

  const handleAddPiece = (name: string) => {
    if (!name.trim()) return;
    if (selectedPieces.includes(name.trim())) {
      triggerAlert('warning', 'Esta parte já está inserida no produto.');
      return;
    }
    setSelectedPieces([...selectedPieces, name.trim()]);
    setCustomPieceInput('');
    setShowCustomPiece(false);
  };

  const handleRemovePiece = (name: string) => {
    setSelectedPieces(selectedPieces.filter(p => p !== name));
  };

  // Unified, high-fidelity edit and creation dialog using native Bootstrap 5 overlay
  const renderEditModal = () => {
    if (!showEditModal || !editingProduct) return null;

    return (
      <div 
        className="modal fade show d-block" 
        style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1060 }} 
        tabIndex={-1} 
        role="dialog"
        id="product-edit-modal"
      >
        <div className="modal-dialog modal-xl modal-dialog-scrollable" role="document">
          <div className="modal-content border-0 rounded-4 shadow-lg overflow-hidden">
            
            <div className="modal-header bg-light border-bottom py-3 px-4 d-flex justify-content-between align-items-center">
              <div>
                <div className="text-secondary text-xs font-mono font-bold uppercase tracking-wider mb-1">
                  {editingProduct.id ? `Catálogo > Editar Produto (ID: ${editingProduct.id})` : 'Catálogo > Novo Produto'}
                </div>
                <h5 className="modal-title fw-black text-dark font-display mb-0">
                  {editingProduct.id ? `Editar Produto: ${editingProduct.name}` : 'Cadastrar novo produto em catálogo'}
                </h5>
              </div>
              <button 
                type="button" 
                className="btn-close" 
                style={{ cursor: 'pointer' }}
                aria-label="Close" 
                onClick={() => { setShowEditModal(false); setEditingProduct(null); }}
              />
            </div>

            <div className="modal-body bg-light-subtle p-4">
              
              {/* Context Alert Feed inside modal */}
              {alert && (
                <div className={`alert alert-${alert.type === 'danger' ? 'danger' : alert.type} d-flex align-items-center gap-2 mb-4 py-2.5 px-3 border-0 rounded`}>
                  <AlertCircle size={18} />
                  <span className="font-sans text-sm font-semibold">{alert.msg}</span>
                </div>
              )}

              {/* Form utilizing standard FormData collection */}
              <form id="editor-add-sub-form" onSubmit={handleSaveProductSubmit} key={editingProduct.id || 'new'}>
                <div className="row g-4 mb-3">
                  
                  {/* Nome do produto input */}
                  <div className="col-12 col-md-8">
                    <label className="form-label text-xxs text-muted font-mono uppercase tracking-widest fw-bold">Nome do produto</label>
                    <input 
                      type="text" 
                      name="name" 
                      maxLength={200}
                      defaultValue={editingProduct.name}
                      required
                      className="form-control form-control-lg bg-light text-dark font-display border-0" 
                      placeholder="Ex: Coifa Industrial Alpha, Curva 45 Inox..." 
                      style={{ borderRadius: '6px', height: '48px' }}
                    />
                    <span className="text-muted font-mono text-xxs text-end d-block mt-1">Limite máx: 200 caracteres</span>
                  </div>

                  {/* Categoria Selector dropdown */}
                  <div className="col-12 col-md-4">
                    <label className="form-label text-xxs text-muted font-mono uppercase tracking-widest fw-bold">Categoria</label>
                    <select 
                      name="category" 
                      className="form-select form-select-lg bg-light text-dark font-display fw-semibold border-0"
                      defaultValue={editingProduct.category}
                      style={{ borderRadius: '6px', height: '48px', cursor: 'pointer' }}
                    >
                      {registeredCategories.map((c) => (
                        <option key={c.id} value={c.name}>
                          {c.parentId ? `└─ ${c.name}` : c.name}
                        </option>
                      ))}
                    </select>
                    <span className="text-muted text-xxs d-block mt-1">Defina a classe técnica do item</span>
                  </div>

                  {/* Imagem do Produto Card */}
                  <div className="col-12 col-md-4">
                    <div className="card-lbl font-mono text-uppercase text-xxs text-muted fw-bold mb-1">Imagem do produto (Final)</div>
                    <div 
                      ref={dragAreaRef1}
                      onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-primary'); }}
                      onDragLeave={(e) => { e.preventDefault(); e.currentTarget.classList.remove('border-primary'); }}
                      onDrop={(e) => { e.currentTarget.classList.remove('border-primary'); handleFileDrop(e, 'productImage'); }}
                      onClick={() => document.getElementById('modal_file_productImage')?.click()}
                      className="card p-3 border-dashed rounded text-center d-flex flex-column justify-content-center align-items-center cursor-pointer bg-white" 
                      style={{ minHeight: '230px', cursor: 'pointer', border: '2px dashed #bbb' }}
                    >
                      {imageState.productImage ? (
                        <div className="w-100 position-relative">
                          <img 
                            src={imageState.productImage} 
                            alt="Product Preview" 
                            className="img-fluid rounded border-0" 
                            style={{ maxHeight: '150px', objectFit: 'contain' }} 
                            referrerPolicy="no-referrer"
                          />
                          <button 
                            type="button" 
                            onClick={(e) => { e.stopPropagation(); handleClearImage('productImage'); }}
                            className="btn btn-sm btn-danger position-absolute top-0 end-0 rounded-circle d-flex align-items-center justify-content-center"
                            style={{ width: '24px', height: '24px' }}
                            title="Deletar Imagem"
                          >
                            <X size={14} />
                          </button>
                          <div className="text-xxs font-mono text-success fw-bold mt-1.5">&#10003; Imagem carregada</div>
                        </div>
                      ) : (
                        <>
                          <Upload className="text-muted mb-2" size={32} />
                          <span className="fw-bold font-sans text-dark text-sm">Imagem do produto</span>
                          <span className="text-muted text-xs font-sans mt-2">Clique para enviar, ou arraste e solte</span>
                          <span className="text-muted font-mono text-xxxs mt-1">MÁXIMO: 550KB | JPG OU PNG</span>
                        </>
                      )}
                      <input 
                        type="file" 
                        id="modal_file_productImage"
                        accept="image/jpeg,image/jpg,image/png"
                        onChange={(e) => handleFileSelect(e, 'productImage')}
                        className="d-none" 
                      />
                    </div>
                  </div>

                  {/* Projeto Desenho 1 Card */}
                  <div className="col-12 col-md-4">
                    <div className="card-lbl font-mono text-uppercase text-xxs text-muted fw-bold mb-1">Projeto Imagem 1 (Desenho)</div>
                    <div 
                      ref={dragAreaRef2}
                      onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-primary'); }}
                      onDragLeave={(e) => { e.preventDefault(); e.currentTarget.classList.remove('border-primary'); }}
                      onDrop={(e) => { e.currentTarget.classList.remove('border-primary'); handleFileDrop(e, 'projectImage1'); }}
                      onClick={() => document.getElementById('modal_file_projectImage1')?.click()}
                      className="card p-3 border-dashed rounded text-center d-flex flex-column justify-content-center align-items-center cursor-pointer bg-white" 
                      style={{ minHeight: '230px', cursor: 'pointer', border: '2px dashed #ccc' }}
                    >
                      {imageState.projectImage1 ? (
                        <div className="w-100 position-relative">
                          <img 
                            src={imageState.projectImage1} 
                            alt="Project 1 Preview" 
                            className="img-fluid rounded" 
                            style={{ maxHeight: '150px', objectFit: 'contain' }} 
                            referrerPolicy="no-referrer"
                          />
                          <button 
                            type="button" 
                            onClick={(e) => { e.stopPropagation(); handleClearImage('projectImage1'); }}
                            className="btn btn-sm btn-danger position-absolute top-0 end-0 rounded-circle d-flex align-items-center justify-content-center"
                            style={{ width: '24px', height: '24px' }}
                            title="Deletar Imagem"
                          >
                            <X size={14} />
                          </button>
                          <div className="text-xxs font-mono text-success fw-bold mt-1.5">&#10003; Desenho 1 Carregado</div>
                        </div>
                      ) : (
                        <>
                          <Upload className="text-muted mb-2" size={32} />
                          <span className="fw-bold font-sans text-dark text-sm">Imagem do Desenho 3D</span>
                          <span className="text-muted text-xs font-sans mt-2">Clique para enviar, ou arraste e solte</span>
                          <span className="text-muted font-mono text-xxxs mt-1">MÁXIMO: 550KB | JPG OU PNG</span>
                        </>
                      )}
                      <input 
                        type="file" 
                        id="modal_file_projectImage1"
                        accept="image/jpeg,image/jpg,image/png"
                        onChange={(e) => handleFileSelect(e, 'projectImage1')}
                        className="d-none" 
                      />
                    </div>
                  </div>

                  {/* Projeto Desenho 2 Card */}
                  <div className="col-12 col-md-4">
                    <div className="card-lbl font-mono text-uppercase text-xxs text-muted fw-bold mb-1">Projeto Imagem 2 (Corte)</div>
                    <div 
                      ref={dragAreaRef3}
                      onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-primary'); }}
                      onDragLeave={(e) => { e.preventDefault(); e.currentTarget.classList.remove('border-primary'); }}
                      onDrop={(e) => { e.currentTarget.classList.remove('border-primary'); handleFileDrop(e, 'projectImage2'); }}
                      onClick={() => document.getElementById('modal_file_projectImage2')?.click()}
                      className="card p-3 border-dashed rounded text-center d-flex flex-column justify-content-center align-items-center cursor-pointer bg-white" 
                      style={{ minHeight: '230px', cursor: 'pointer', border: '2px dashed #ccc' }}
                    >
                      {imageState.projectImage2 ? (
                        <div className="w-100 position-relative">
                          <img 
                            src={imageState.projectImage2} 
                            alt="Project 2 Preview" 
                            className="img-fluid rounded" 
                            style={{ maxHeight: '150px', objectFit: 'contain' }} 
                            referrerPolicy="no-referrer"
                          />
                          <button 
                            type="button" 
                            onClick={(e) => { e.stopPropagation(); handleClearImage('projectImage2'); }}
                            className="btn btn-sm btn-danger position-absolute top-0 end-0 rounded-circle d-flex align-items-center justify-content-center"
                            style={{ width: '24px', height: '24px' }}
                            title="Deletar Imagem"
                          >
                            <X size={14} />
                          </button>
                          <div className="text-xxs font-mono text-success fw-bold mt-1.5">&#10003; Desenho 2 Carregado</div>
                        </div>
                      ) : (
                        <>
                          <Upload className="text-muted mb-2" size={32} />
                          <span className="fw-bold font-sans text-dark text-sm">Imagem do Desenho Técnico</span>
                          <span className="text-muted text-xs font-sans mt-2">Clique para enviar, ou arraste e solte</span>
                          <span className="text-muted font-mono text-xxxs mt-1">MÁXIMO: 550KB | JPG OU PNG</span>
                        </>
                      )}
                      <input 
                        type="file" 
                        id="modal_file_projectImage2"
                        accept="image/jpeg,image/jpg,image/png"
                        onChange={(e) => handleFileSelect(e, 'projectImage2')}
                        className="d-none" 
                      />
                    </div>
                  </div>

                  {/* Descrição textarea */}
                  <div className="col-12 col-lg-8">
                    <div className="card shadow-sm bg-white p-4 rounded h-100 border">
                      <label className="form-label text-xxs text-muted font-mono uppercase tracking-widest fw-bold">Descrição do produto</label>
                      <textarea 
                        name="desc" 
                        rows={8}
                        defaultValue={editingProduct.desc}
                        className="form-control bg-light border-0" 
                        placeholder="Ex: Produzida em aço inox 430 de espessura 08mm..." 
                        style={{ borderRadius: '6px', fontSize: '1rem', lineHeight: '1.5' }}
                      />
                    </div>
                  </div>

                  {/* Right Column (Acessarios, Modelo, Partes) */}
                  <div className="col-12 col-lg-4">
                    <div className="d-flex flex-column gap-3 h-100 justify-content-between">
                      
                      {/* Acessórios selector */}
                      <div className="card shadow-sm bg-white p-3 rounded flex-grow-1 border">
                        <span className="text-xxs text-muted font-mono uppercase tracking-widest fw-bold mb-2 d-block">Acessórios Recomendados</span>
                        
                        <div className="d-flex flex-wrap gap-1 mb-3">
                          {selectedAccessories.length > 0 ? (
                            selectedAccessories.map(acc => (
                              <span key={acc} className="badge bg-secondary bg-opacity-25 text-dark font-sans text-xs d-flex align-items-center gap-1.5 px-2 py-1.5 rounded-pill">
                                {acc}
                                <X size={12} className="cursor-pointer text-danger" onClick={() => handleRemoveAccessory(acc)} />
                              </span>
                            ))
                          ) : (
                            <span className="text-muted text-xs font-mono d-block my-1">Nenhum acessório selecionado</span>
                          )}
                        </div>

                        <div className="d-flex flex-column gap-2">
                          <select 
                            className="form-select form-select-sm" 
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === 'custom') {
                                setShowCustomAccessory(true);
                              } else if (val) {
                                handleAddAccessory(val);
                              }
                              e.target.value = '';
                            }}
                            style={{ cursor: 'pointer' }}
                          >
                            <option value="">+ Selecionar do Catálogo</option>
                            {registeredAccessoryProducts.map(acc => (
                              <option key={acc.id} value={acc.name}>{acc.name} ({acc.model})</option>
                            ))}
                            <option value="custom">[+] Digitar nome personalizado...</option>
                          </select>

                          {showCustomAccessory && (
                            <div className="input-group input-group-sm mt-1">
                              <input 
                                type="text" 
                                value={customAccessoryInput}
                                onChange={(e) => setCustomAccessoryInput(e.target.value)}
                                placeholder="Nome do acessório" 
                                className="form-control"
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddAccessory(customAccessoryInput); } }}
                              />
                              <button type="button" className="btn btn-primary" onClick={() => handleAddAccessory(customAccessoryInput)}>OK</button>
                              <button type="button" className="btn btn-light" onClick={() => setShowCustomAccessory(false)}>X</button>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Modelo Input */}
                      <div className="card shadow-sm bg-white p-3 rounded border">
                        <label className="form-label text-xxs text-muted font-mono uppercase tracking-widest fw-bold">Modelo</label>
                        <input 
                          type="text" 
                          name="model" 
                          defaultValue={editingProduct.model}
                          className="form-control bg-light border-0" 
                          placeholder="Ex: Alpha R-10" 
                          style={{ borderRadius: '6px' }}
                        />
                      </div>

                      {/* Código ERP (ESW) Input */}
                      <div className="card shadow-sm bg-white p-3 rounded border">
                        <label className="form-label text-xxs text-muted font-mono uppercase tracking-widest fw-bold">Código no ERP (ESW)</label>
                        <input 
                          type="text" 
                          name="erpProductId" 
                          defaultValue={editingProduct.erpProductId || ''}
                          className="form-control bg-light border-0 fw-bold font-mono text-primary" 
                          placeholder="Ex: 1060" 
                          style={{ borderRadius: '6px' }}
                        />
                        <span className="text-muted text-xxs d-block mt-1">ID do produto no ERP para associação automática de pedidos</span>
                      </div>

                      {/* Partes selector */}
                      <div className="card shadow-sm bg-white p-3 rounded border">
                        <span className="text-xxs text-muted font-mono uppercase tracking-widest fw-bold mb-2 d-block">Partes Estruturais</span>
                        
                        <div className="d-flex flex-wrap gap-1 mb-3">
                          {selectedPieces.length > 0 ? (
                            selectedPieces.map(piece => (
                              <span key={piece} className="badge bg-info bg-opacity-25 text-dark font-sans text-xs d-flex align-items-center gap-1.5 px-2 py-1.5 rounded-pill">
                                {piece}
                                <X size={12} className="cursor-pointer text-danger" onClick={() => handleRemovePiece(piece)} />
                              </span>
                            ))
                          ) : (
                            <span className="text-muted text-xs font-mono d-block my-1">Nenhuma parte vinculada</span>
                          )}
                        </div>

                        <div className="d-flex flex-column gap-2">
                          <select 
                            className="form-select form-select-sm" 
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === 'custom') {
                                setShowCustomPiece(true);
                              } else if (val) {
                                handleAddPiece(val);
                              }
                              e.target.value = '';
                            }}
                            style={{ cursor: 'pointer' }}
                          >
                            <option value="">+ Selecionar do Catálogo</option>
                            {registeredPieceProducts.map(p => (
                              <option key={p.id} value={p.name}>{p.name} ({p.model})</option>
                            ))}
                            <option value="custom">[+] Digitar nome de parte...</option>
                          </select>

                          {showCustomPiece && (
                            <div className="input-group input-group-sm mt-1">
                              <input 
                                type="text" 
                                value={customPieceInput}
                                onChange={(e) => setCustomPieceInput(e.target.value)}
                                placeholder="Nome da parte/peça" 
                                className="form-control"
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddPiece(customPieceInput); } }}
                              />
                              <button type="button" className="btn btn-primary" onClick={() => handleAddPiece(customPieceInput)}>OK</button>
                              <button type="button" className="btn btn-light" onClick={() => setShowCustomPiece(false)}>X</button>
                            </div>
                          )}
                        </div>
                      </div>

                    </div>
                  </div>

                  {/* Sequencia de produçao checkboxes sectors */}
                  <div className="col-12">
                    <div className="card shadow-sm bg-white p-4 rounded border">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h5 className="fw-bold font-sans text-dark mb-0">Sequência de produção (Setores por onde o produto passa)</h5>
                        <span className="badge bg-primary bg-opacity-10 text-primary font-mono text-xs px-2 py-1 rounded">
                          {productionSectors.length} Setores de Produção
                        </span>
                      </div>
                      <p className="text-muted text-xs mb-3 font-sans">
                        Selecione as etapas necessárias para fabricar ou registrar o tempo nas ordens de serviço. Os setores são carregados do banco de dados na ordem da esteira fabril.
                      </p>
                      
                      <div className="d-flex flex-wrap gap-3">
                        {(productionSectors.length > 0 ? productionSectors : [
                          { id: '01', name: 'Corte', colorHex: '#0d6efd', orderIndex: 1, isProductionSequence: true, status: 'ATIVO', permissions: {} as any },
                          { id: '02', name: 'Dobra', colorHex: '#fd7e14', orderIndex: 2, isProductionSequence: true, status: 'ATIVO', permissions: {} as any },
                          { id: '03', name: 'Solda', colorHex: '#0d6efd', orderIndex: 3, isProductionSequence: true, status: 'ATIVO', permissions: {} as any },
                          { id: '04', name: 'Acabamento', colorHex: '#198754', orderIndex: 4, isProductionSequence: true, status: 'ATIVO', permissions: {} as any },
                          { id: '05', name: 'Acessórios', colorHex: '#0dcaf0', orderIndex: 5, isProductionSequence: true, status: 'ATIVO', permissions: {} as any },
                          { id: '06', name: 'Embalagem', colorHex: '#6c757d', orderIndex: 6, isProductionSequence: true, status: 'ATIVO', permissions: {} as any },
                          { id: '07', name: 'Logística', colorHex: '#6c757d', orderIndex: 7, isProductionSequence: true, status: 'ATIVO', permissions: {} as any },
                        ]).map((sec) => {
                          const isChecked = editingProduct.productionSequence?.includes(sec.name) ?? true;
                          return (
                            <div 
                              key={sec.id || sec.name} 
                              className="form-check d-flex align-items-center gap-2 bg-light py-2 px-3 border rounded shadow-xs" 
                              style={{ cursor: 'pointer', transition: 'all 0.15s ease' }}
                            >
                              <input 
                                className="form-check-input mt-0" 
                                type="checkbox" 
                                name={`sequence_${sec.name}`}
                                id={`modal-step-chk-${sec.id || sec.name}`} 
                                defaultChecked={isChecked}
                                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                              />
                              <label 
                                className="form-check-label text-sm fw-bold text-dark font-sans d-flex align-items-center gap-1.5 ms-1" 
                                htmlFor={`modal-step-chk-${sec.id || sec.name}`} 
                                style={{ userSelect: 'none', cursor: 'pointer' }}
                              >
                                <span 
                                  className="rounded-circle d-inline-block shadow-2xs" 
                                  style={{ width: '10px', height: '10px', backgroundColor: sec.colorHex || '#0d6efd' }}
                                />
                                {sec.name}
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                </div>
              </form>

            </div>

            <div className="modal-footer bg-light border-top py-3 px-4 d-flex justify-content-end gap-2.5">
              <button 
                type="button" 
                className="btn btn-outline-secondary px-4 py-1.5 font-display text-sm fw-semibold"
                style={{ borderRadius: '6px' }}
                onClick={() => { setShowEditModal(false); setEditingProduct(null); }}
              >
                Cancelar
              </button>
              <button 
                type="submit" 
                form="editor-add-sub-form"
                className="btn btn-primary px-4 py-1.5 font-display text-sm text-white fw-bold"
                style={{ backgroundColor: '#2f66aa', borderColor: '#2f66aa', borderRadius: '6px' }}
              >
                {editingProduct.id ? 'Salvar Alterações' : 'Cadastrar Produto'}
              </button>
            </div>

          </div>
        </div>
      </div>
    );
  };

  // -------------------------------------------------------------
  // VIEW RENDER A: DETAIL VIEW (PAGE 7 VISUAL REPLICA)
  // -------------------------------------------------------------
  if (viewState === 'DETAIL' && selectedProduct) {
    return (
      <div className="fade-in px-1 container-fluid py-3" id="product-detail-view-container">
        
        {/* Alerts panel */}
        {alert && (
          <div className={`alert alert-${alert.type === 'danger' ? 'danger' : alert.type} d-flex align-items-center gap-2 mb-4 py-2 px-3 border-0 rounded`} role="alert">
            <AlertCircle size={18} />
            <span className="font-sans text-sm font-medium">{alert.msg}</span>
          </div>
        )}

        {/* Breadcrumbs path */}
        <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center border-bottom pb-3 mb-4 gap-2">
          <div>
            <div className="text-secondary text-xs font-mono font-bold uppercase tracking-wider mb-1">
              Produtos &gt; Lista de produtos &gt; {selectedProduct.name}
            </div>
            <h1 className="h4 fw-black text-dark font-display mb-0">{selectedProduct.name}</h1>
          </div>
          
          <div className="d-flex gap-2">
            <button 
              onClick={() => setViewState('LIST')}
              className="btn btn-outline-secondary d-flex align-items-center gap-1 text-sm font-display py-1.5 px-3"
              type="button"
            >
              <ArrowLeft size={16} /> Voltar
            </button>
            {canEditProduto && (
              <button 
                onClick={() => handleEditClick(selectedProduct)}
                className="btn btn-primary d-flex align-items-center gap-1.5 text-sm font-display py-1.5 px-3"
                type="button"
                style={{ backgroundColor: '#2f66aa', borderColor: '#2f66aa' }}
              >
                Editar Produto
              </button>
            )}
            {canDeleteProduto && (
              <button 
                onClick={() => handleDeleteProduct(selectedProduct.id, selectedProduct.name)}
                className="btn btn-outline-danger d-flex align-items-center gap-1 text-sm font-display py-1.5 px-3"
                type="button"
              >
                <Trash2 size={16} /> Excluir
              </button>
            )}
          </div>
        </div>

        {/* Product view detailed info layout */}
        <div className="row g-4 mb-4">
          
          {/* Box "Imagem do Item" */}
          <div className="col-12 col-md-5">
            <div 
              className="card border shadow-sm d-flex justify-content-center align-items-center text-center p-3 rounded position-relative"
              style={{ backgroundColor: '#fafafa', minHeight: '340px' }}
            >
              {selectedProduct.productImage ? (
                <div 
                  className="w-full text-center position-relative cursor-zoom-in"
                  style={{ cursor: 'zoom-in' }}
                  onClick={() => setExpandedImage({ src: selectedProduct.productImage, title: `Imagem do Produto: ${selectedProduct.name}` })}
                  title="Clique para expandir a imagem"
                >
                  <img 
                    src={selectedProduct.productImage} 
                    alt={selectedProduct.name} 
                    className="img-fluid rounded border shadow-sm img-zoom-hover" 
                    style={{ maxHeight: '300px', objectFit: 'contain', transition: 'all 0.2s ease-in-out' }}
                    referrerPolicy="no-referrer"
                  />
                  <div className="text-xxs font-sans text-primary mt-2 fw-bold d-flex align-items-center justify-content-center gap-1">
                    <Search size={12} /> Clique para ampliar imagem do catálogo
                  </div>
                </div>
              ) : (
                <div>
                  {/* SVG Coifa Icon representation */}
                  <svg viewBox="0 0 64 36" width="120" height="70" className="opacity-25" fill="none" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M 8 32 L 20 6 L 44 6 L 56 32 Z" fill="#b0b0b0" stroke="#444" />
                    <rect x="23" y="0" width="18" height="6" fill="#d0d0d0" stroke="#444" />
                    <line x1="8" y1="32" x2="56" y2="32" stroke="#444" strokeWidth="3" />
                  </svg>
                  <h6 className="fw-bold text-muted font-sans mt-3">Imagem do ítem</h6>
                  <p className="text-muted text-xs mb-0 font-mono">Nenhuma imagem carregada ainda</p>
                </div>
              )}
            </div>
          </div>

          {/* Details side text info */}
          <div className="col-12 col-md-7">
            <div className="card border shadow-sm p-4 bg-white h-100 rounded">
              <div className="row">
                <div className="col-6 mb-3">
                  <span className="text-xxs text-muted font-mono uppercase tracking-widest d-block">Categoria</span>
                  <span className="badge bg-primary bg-opacity-10 text-dark border font-mono text-xs px-2.5 py-1.5" style={{ borderRadius: '4px' }}>
                    {selectedProduct.category}
                  </span>
                </div>

                <div className="col-6 mb-3">
                  <span className="text-xxs text-muted font-mono uppercase tracking-widest d-block">Modelo</span>
                  <span className="fw-bold text-dark font-sans text-base">{selectedProduct.model || 'Standard'}</span>
                </div>

                <div className="col-6 mb-3">
                  <span className="text-xxs text-muted font-mono uppercase tracking-widest d-block">Código do Controle</span>
                  <span className="font-mono text-dark text-base">{selectedProduct.id.padStart(4, '0')}</span>
                </div>

                <div className="col-6 mb-3">
                  <span className="text-xxs text-muted font-mono uppercase tracking-widest d-block">Mapeamento ERP (ESW)</span>
                  {selectedProduct.erpProductId ? (
                    <span className="badge bg-success bg-opacity-25 text-success border border-success font-mono text-xs px-2.5 py-1.5" style={{ borderRadius: '4px' }}>
                      Mapeado: #{selectedProduct.erpProductId}
                    </span>
                  ) : (
                    <span className="badge bg-warning bg-opacity-25 text-dark border border-warning font-mono text-xs px-2.5 py-1.5" style={{ borderRadius: '4px' }}>
                      Não Mapeado
                    </span>
                  )}
                </div>

                <div className="col-12 mb-3">
                  <span className="text-xxs text-muted font-mono uppercase tracking-widest d-block mb-1">Descritivo:</span>
                  <div className="bg-light p-3 rounded font-mono text-secondary text-xs" style={{ whiteSpace: 'pre-line', lineHeight: '1.4' }}>
                    {selectedProduct.desc || "Nenhuma descrição fornecida."}
                  </div>
                </div>

                {/* Sequência de produção viewer */}
                <div className="col-12 mb-3">
                  <span className="text-xxs text-muted font-mono uppercase tracking-widest d-block mb-1">Roteiro / Caminho de Produção:</span>
                  <div className="d-flex flex-wrap gap-1.5 align-items-center">
                    {(selectedProduct.productionSequence || []).map((step, stepId) => {
                      const matchedSector最佳 = productionSectors.find(s => s.name.trim().toLowerCase() === step.trim().toLowerCase());
                      const stepColor = matchedSector最佳?.colorHex || '#212529';
                      return (
                        <React.Fragment key={step}>
                          <span 
                            className="badge text-white font-sans text-xs px-2.5 py-1.5 rounded d-inline-flex align-items-center gap-1.5 shadow-2xs"
                            style={{ backgroundColor: stepColor }}
                          >
                            <span className="rounded-circle bg-white bg-opacity-75" style={{ width: '6px', height: '6px' }} />
                            {step}
                          </span>
                          {stepId < (selectedProduct.productionSequence || []).length - 1 && (
                            <span className="text-muted font-mono text-xs px-0.5">&gt;</span>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>

                {/* Accessories Column */}
                <div className="col-12 col-sm-6 mb-3">
                  <span className="text-xxs text-muted font-mono uppercase tracking-widest d-block mb-2">Acessórios recomendados (Clique para abrir):</span>
                  <div className="d-flex flex-wrap gap-2">
                    {selectedProduct.accessories && selectedProduct.accessories.length > 0 ? (
                      selectedProduct.accessories.map((acc, index) => (
                        <button 
                          key={index} 
                          type="button"
                          onClick={() => handleAccessoryOrPieceClick(acc, 'Acessórios')}
                          className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1 text-xs px-2.5 py-1 text-dark border-secondary border-opacity-25"
                          style={{ borderRadius: '6px', fontSize: '0.8rem', backgroundColor: '#fdfdfd' }}
                          title={`Visualizar especificações estruturais de ${acc}`}
                        >
                          <span className="w-1.5 h-1.5 rounded-circle bg-secondary d-inline-block"></span>
                          <span className="fw-semibold">{acc}</span>
                        </button>
                      ))
                    ) : (
                      <span className="text-muted text-xs font-mono py-1">Nenhum acessório adicional</span>
                    )}
                  </div>
                </div>

                {/* Partes Column */}
                <div className="col-12 col-sm-6 mb-3">
                  <span className="text-xxs text-muted font-mono uppercase tracking-widest d-block mb-2">Partes estruturais / Peças (Clique para abrir):</span>
                  <div className="d-flex flex-wrap gap-2">
                    {selectedProduct.pieces && selectedProduct.pieces.length > 0 ? (
                      selectedProduct.pieces.map((part, index) => (
                        <button 
                          key={index} 
                          type="button"
                          onClick={() => handleAccessoryOrPieceClick(part, 'Partes')}
                          className="btn btn-sm btn-outline-info d-flex align-items-center gap-1 text-xs px-2.5 py-1 text-dark border-info border-opacity-25"
                          style={{ borderRadius: '6px', fontSize: '0.8rem', backgroundColor: '#fcfdfd' }}
                          title={`Visualizar especificações estruturais de ${part}`}
                        >
                          <span className="w-1.5 h-1.5 rounded-circle bg-info d-inline-block"></span>
                          <span className="fw-semibold">{part}</span>
                        </button>
                      ))
                    ) : (
                      <span className="text-muted text-xs font-mono py-1">Nenhuma parte cadastrada</span>
                    )}
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section: Imagens do Desenho/Projeto com click p/ ampliação máxima */}
        <div className="card shadow-sm border bg-white p-4 rounded mb-5" id="images-project-panels">
          <div className="border-bottom pb-2 mb-4 d-flex justify-content-between align-items-center">
            <h5 className="fw-black font-display text-dark mb-0">Imagens do Desenho e Projeto Técnicos</h5>
            <span className="badge bg-light text-secondary border font-mono text-xs">Exclusivo Operador &amp; Conferência</span>
          </div>
          
          <div className="row g-4 text-center">
            <div className="col-12 col-sm-6">
              <div 
                className={`p-3 border rounded-3 d-flex flex-column justify-content-center align-items-center bg-light position-relative ${selectedProduct.projectImage1 ? 'cursor-zoom-in' : ''}`}
                style={{ minHeight: '260px', cursor: selectedProduct.projectImage1 ? 'zoom-in' : 'default', transition: 'all 0.15s ease-in-out' }}
                onClick={() => {
                  if (selectedProduct.projectImage1) {
                    setExpandedImage({ src: selectedProduct.projectImage1, title: `Desenho Técnico de Planta 1 - ${selectedProduct.name}` });
                  }
                }}
                title={selectedProduct.projectImage1 ? "Clique para expandir o Desenho Técnico da Planta 1" : "Sem imagem cadastrada"}
              >
                <span className="position-absolute font-display fw-bold text-secondary text-5xl" style={{ top: '15px', right: '25px', opacity: 0.15 }}>1</span>
                {selectedProduct.projectImage1 ? (
                  <div className="w-100 text-center">
                    <img 
                      src={selectedProduct.projectImage1} 
                      alt="Desenho Tecnico 1" 
                      className="img-fluid rounded border shadow-sm img-zoom-hover" 
                      style={{ maxHeight: '220px', objectFit: 'contain' }}
                      referrerPolicy="no-referrer"
                    />
                    <div className="text-xxs font-sans text-primary mt-2 fw-bold d-flex align-items-center justify-content-center gap-1">
                      <Search size={11} /> Clique para ampliar o projeto técnico (Planta 1)
                    </div>
                  </div>
                ) : (
                  <>
                    <svg viewBox="0 0 64 64" width="80" height="80" className="opacity-15 mb-2" fill="none" stroke="#222">
                      <path d="M 5 50 L 25 20 L 40 38 L 48 30 L 59 50 Z" fill="#ccc" />
                      <circle cx="20" cy="15" r="5" fill="#f0a020" />
                    </svg>
                    <span className="text-muted text-sm font-sans fw-semibold">Desenho Técnico de Planta 1</span>
                    <span className="text-muted text-xxs font-mono mt-1">Nenhum projeto inserido para conferência</span>
                  </>
                )}
              </div>
            </div>

            <div className="col-12 col-sm-6">
              <div 
                className={`p-3 border rounded-3 d-flex flex-column justify-content-center align-items-center bg-light position-relative ${selectedProduct.projectImage2 ? 'cursor-zoom-in' : ''}`}
                style={{ minHeight: '260px', cursor: selectedProduct.projectImage2 ? 'zoom-in' : 'default', transition: 'all 0.15s ease-in-out' }}
                onClick={() => {
                  if (selectedProduct.projectImage2) {
                    setExpandedImage({ src: selectedProduct.projectImage2, title: `Visualização de Corte 2 - ${selectedProduct.name}` });
                  }
                }}
                title={selectedProduct.projectImage2 ? "Clique para expandir a Visualização de Corte 2" : "Sem imagem cadastrada"}
              >
                <span className="position-absolute font-display fw-bold text-secondary text-5xl" style={{ top: '15px', right: '25px', opacity: 0.15 }}>2</span>
                {selectedProduct.projectImage2 ? (
                  <div className="w-100 text-center">
                    <img 
                      src={selectedProduct.projectImage2} 
                      alt="Desenho Tecnico 2" 
                      className="img-fluid rounded border shadow-sm img-zoom-hover" 
                      style={{ maxHeight: '220px', objectFit: 'contain' }}
                      referrerPolicy="no-referrer"
                    />
                    <div className="text-xxs font-sans text-primary mt-2 fw-bold d-flex align-items-center justify-content-center gap-1">
                      <Search size={11} /> Clique para ampliar o projeto de corte (Visualização 2)
                    </div>
                  </div>
                ) : (
                  <>
                    <svg viewBox="0 0 64 64" width="80" height="80" className="opacity-15 mb-2" fill="none" stroke="#222">
                      <path d="M 5 50 L 25 20 L 40 38 L 48 30 L 59 50 Z" fill="#ccc" />
                      <circle cx="20" cy="15" r="5" fill="#f0a020" />
                    </svg>
                    <span className="text-muted text-sm font-sans fw-semibold">Visualização de Corte 2</span>
                    <span className="text-muted text-xxs font-mono mt-1">Nenhum projeto inserido para conferência</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* LIGHTBOX MODAL PARA AMPLIAÇÃO MÁXIMA DE IMAGENS DE DETALHES DE PROJETO */}
        {expandedImage && (
          <div 
            className="position-fixed top-0 start-0 w-100 h-100 d-flex flex-column justify-content-center align-items-center fade-in"
            style={{
              backgroundColor: 'rgba(11, 16, 23, 0.96)',
              zIndex: 10000,
              backdropFilter: 'blur(12px)',
              transition: 'all 0.25s ease'
            }}
            onClick={() => setExpandedImage(null)}
          >
            {/* Header com os controles do Operador no Chão de Fábrica */}
            <div className="position-absolute top-0 start-0 w-100 p-3 d-flex justify-content-between align-items-center bg-black bg-opacity-75 border-bottom border-secondary border-opacity-25" style={{ zIndex: 10002 }}>
              <div className="d-flex align-items-center gap-2">
                <span className="badge bg-warning text-dark font-mono text-xxs px-2 py-1 uppercase tracking-wider">CONFERÊNCIA DETALHES</span>
                <span className="font-display fw-bold text-white text-sm uppercase mb-0">{expandedImage.title}</span>
              </div>
              <button 
                onClick={() => setExpandedImage(null)} 
                className="btn btn-outline-light btn-sm d-flex align-items-center justify-content-center" 
                style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.08)' }}
                title="Fechar Ampliação (ESC)"
              >
                <X size={22} />
              </button>
            </div>

            {/* Imagem em proporções máximas */}
            <div className="p-3 d-flex justify-content-center align-items-center" style={{ width: '95vw', height: '82vh', zIndex: 10001, marginTop: '60px' }} onClick={(e) => e.stopPropagation()}>
              <img 
                src={expandedImage.src} 
                alt={expandedImage.title} 
                className="img-fluid rounded border border-secondary border-opacity-20 shadow-lg select-none"
                style={{
                  maxHeight: '100%',
                  maxWidth: '100%',
                  objectFit: 'contain',
                  backgroundColor: '#ffffff',
                  boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)'
                }}
                referrerPolicy="no-referrer"
              />
            </div>

            {/* Chão de fábrica helper instruction */}
            <div className="position-absolute bottom-0 w-100 text-center p-3 text-white-50 font-sans text-xs bg-black bg-opacity-50">
              Dica de Produção: No tablet, use o movimento de pinça com dois dedos para fazer zoom no desenho de detalhe, ou use o scroll no desktop. Clique fora para voltar.
            </div>
          </div>
        )}

        {/* Dynamic creation and edit overlay */}
        {renderEditModal()}

      </div>
    );
  }

  // -------------------------------------------------------------
  // VIEW RENDER C: STANDARD PRODUCT CATALOG GRID (PAGE 6 REPLICA)
  // -------------------------------------------------------------
  return (
    <div className="fade-in px-1 px-sm-2 container-fluid py-3" id="products-catalog-page">
      
      {/* Title */}
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center border-bottom pb-3 mb-4 gap-2">
        <div>
          <div className="text-secondary text-xs font-mono font-bold uppercase tracking-wider mb-1">
            Produtos &gt; Lista de produtos
          </div>
          <h1 className="h3 fw-black text-dark font-display mb-0 text-lowercase" style={{ letterSpacing: '-0.5px' }}>
            Lista de produtos
          </h1>
        </div>
        
        {canAddProduto && (
          <button 
            onClick={handleAddClick}
            className="btn btn-primary d-flex align-items-center justify-content-center gap-1.5 px-3 py-2.5 font-display text-sm text-white font-semibold w-100 w-sm-auto mb-2 mb-sm-0"
            style={{ backgroundColor: '#2f66aa', borderColor: '#2f66aa', borderRadius: '6px' }}
            type="button"
          >
            <Plus size={16} /> Adicionar produto
          </button>
        )}
      </div>

      {/* Dynamic Alerts inside general view */}
      {alert && (
        <div className={`alert alert-${alert.type === 'danger' ? 'danger' : alert.type} d-flex align-items-center gap-2 mb-4 py-2 px-3 border-0 rounded`} role="alert">
          <AlertCircle size={18} />
          <span className="font-sans text-sm font-medium">{alert.msg}</span>
        </div>
      )}

      {/* Page 6 Filter controls Card */}
      <div className="card shadow-sm border bg-white p-3 mb-4 rounded-3" id="catalog-filter-wrapper">
        <div className="row g-3">
          
          <div className="col-12 col-md-3">
            <label className="form-label text-xxs text-muted text-uppercase fw-extrabold tracking-wider font-mono mb-1">Filtrar por Categoria</label>
            <select 
              className="form-select font-sans py-2 fw-semibold"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              style={{ borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem' }}
            >
              <option value="Todas">Todas</option>
              {registeredCategories.map(c => (
                <option key={c.id} value={c.name}>{c.name}</option>
              ))}
            </select>
          </div>

          <div className="col-12 col-md-5">
            <label className="form-label text-xxs text-muted text-uppercase fw-extrabold tracking-wider font-mono mb-1">Nome do produto</label>
            <input 
              type="text" 
              className="form-control py-2"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Digite o nome do produto..." 
              style={{ borderRadius: '6px', fontSize: '0.85rem' }}
            />
          </div>

          <div className="col-12 col-md-4">
            <label className="form-label text-xxs text-muted text-uppercase fw-extrabold tracking-wider font-mono mb-1">Código/Modelo</label>
            <input 
              type="text" 
              className="form-control py-2"
              placeholder="Código ou modelo..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ borderRadius: '6px', fontSize: '0.85rem' }}
            />
          </div>
          
          <div className="col-12 d-flex justify-content-end gap-2 mt-2">
            <button 
              onClick={() => { setSearchQuery(''); setCategoryFilter('Todas'); }}
              className="btn btn-outline-secondary py-2 px-3 flex-grow-1 flex-md-grow-0 font-display"
              type="button"
              style={{ borderRadius: '6px', fontSize: '0.85rem' }}
            >
              Limpar Filtros
            </button>
            <button 
              className="btn btn-primary py-2 px-4 flex-grow-1 flex-md-grow-0 font-display text-white"
              style={{ backgroundColor: '#2f66aa', borderColor: '#2f66aa', borderRadius: '6px', fontSize: '0.85rem' }}
              type="button"
            >
              Aplicar Filtros
            </button>
          </div>

        </div>
      </div>

      {/* Page 6 Products List Table */}
      <div className="card shadow-sm border bg-white rounded overflow-hidden mb-5">
        {/* Tabela de Produtos para PC/Telas Grandes */}
        <div className="table-responsive d-none d-lg-block">
          <table className="table table-hover align-middle mb-0" style={{ fontSize: '0.88rem' }}>
            <thead className="table-light">
              <tr className="border-bottom">
                <th className="px-4 py-2.5 fw-bold text-secondary text-uppercase text-xs" style={{ width: '120px' }}>Código</th>
                <th className="py-2.5 fw-bold text-secondary text-uppercase text-xs">Produto</th>
                <th className="px-4 py-2.5 fw-bold text-secondary text-uppercase text-xs text-end" style={{ width: '180px' }}>Categoria</th>
              </tr>
            </thead>
            <tbody>
              {productsList.length === 0 ? (
                <tr>
                  <td colSpan={3} className="text-center py-5 text-muted">
                    Nenhum produto cadastrado com os critérios especificados.
                  </td>
                </tr>
              ) : (
                productsList.map((p) => (
                  <tr 
                    key={p.id} 
                    onClick={() => handleRowClick(p.id)}
                    style={{ cursor: 'pointer' }}
                    title="Clique para ver detalhamento do produto de catálogo"
                    className="border-bottom"
                  >
                    <td className="px-4 py-3 font-mono text-dark fw-bold">
                      {p.id.padStart(3, '0')}
                    </td>
                    <td className="py-2">
                      <div className="d-flex align-items-center gap-3">
                        <div className="rounded border d-flex align-items-center justify-content-center bg-light" style={{ width: '55px', height: '38px' }}>
                          {p.productImage ? (
                            <img 
                              src={p.productImage} 
                              alt={p.name} 
                              style={{ width: '100%', height: '100%', objectFit: 'contain' }} 
                              referrerPolicy="no-referrer"
                            />
                          ) : (
                            <svg viewBox="0 0 64 36" width="48" height="28" fill="none" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M 12 30 L 22 8 L 42 8 L 52 30 Z" fill="#b0b0b0" stroke="#444" />
                              <rect x="25" y="0" width="14" height="8" fill="#d0d0d0" stroke="#444" />
                              <line x1="12" y1="30" x2="52" y2="30" stroke="#444" strokeWidth="3" />
                            </svg>
                          )}
                        </div>
                        <div>
                          <span className="fw-semibold text-primary font-sans d-block">
                            {p.name}
                          </span>
                          <span className="text-secondary text-xxs font-mono">{p.model || 'Standard'}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-end font-sans">
                      <span className="badge bg-secondary bg-opacity-15 text-dark border">
                        {p.category}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Visualização de Cartões Mobile para Celulares e Tablets em PWA (d-block d-lg-none) */}
        <div className="d-block d-lg-none p-2 bg-light-subtle" id="mobile-products-cards-list">
          {productsList.length === 0 ? (
            <div className="text-center py-5 text-muted font-mono text-xs bg-white rounded">
              Nenhum produto cadastrado com os critérios especificados.
            </div>
          ) : (
            productsList.map((p) => (
              <div 
                key={p.id} 
                className="card mb-2 shadow-sm border border-secondary border-opacity-15 rounded-3 bg-white"
                onClick={() => handleRowClick(p.id)}
                style={{ cursor: 'pointer', minHeight: '48px' }}
                title="Clique para ver detalhamento do produto de catálogo"
              >
                <div className="card-body p-3">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <span className="badge bg-dark text-warning font-mono text-xxs px-2 py-1 rounded">
                      CÓD {p.id.padStart(3, '0')}
                    </span>
                    <span className="badge bg-light text-dark font-sans text-xxs border px-2 py-1 rounded">
                      {p.category}
                    </span>
                  </div>

                  <div className="d-flex align-items-center gap-3">
                    <div className="rounded border d-flex align-items-center justify-content-center bg-light-subtle" style={{ width: '60px', height: '42px', flexShrink: 0 }}>
                      {p.productImage ? (
                        <img 
                          src={p.productImage} 
                          alt={p.name} 
                          style={{ width: '100%', height: '100%', objectFit: 'contain' }} 
                          referrerPolicy="no-referrer"
                        />
                      ) : (
                        <svg viewBox="0 0 64 36" width="48" height="28" fill="none" stroke="#606060" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M 12 30 L 22 8 L 42 8 L 52 30 Z" fill="#c0c0c0" stroke="#555" />
                          <rect x="25" y="0" width="14" height="8" fill="#d8d8d8" stroke="#555" />
                          <line x1="12" y1="30" x2="52" y2="30" stroke="#555" strokeWidth="3" />
                        </svg>
                      )}
                    </div>
                    
                    <div className="flex-grow-1 min-w-0">
                      <h6 className="fw-bold text-primary font-sans text-sm mb-0 text-truncate text-wrap">
                        {p.name}
                      </h6>
                      <span className="text-secondary text-xxs font-mono d-block mt-0.5">{p.model || 'Standard'}</span>
                    </div>
                  </div>

                  <div className="d-flex justify-content-end align-items-center mt-2.5 pt-2 border-top border-light">
                    <span className="text-xxs font-mono text-uppercase fw-bold text-muted d-inline-flex align-items-center gap-1">
                      Ver Ficha Técnica &bull; Toque para abrir
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Dynamic creation and edit overlay */}
      {renderEditModal()}

    </div>
  );
}
